import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  type SmartConfigBody,
  type SmartConfigResponse,
  type SmartKind,
  type SmartLoad,
  type SmartMode,
  type SmartRunsResponse,
  clearSmartRuns,
  fetchSmartConfig,
  fetchSmartRuns,
  resetSmartConfig,
  saveSmartConfig,
} from "../api";
import { EvSettings } from "../components/EvSettings";
import { formatEnergy, formatEur } from "../format";
import { ScreenHeader, SectionTitle } from "../components/ui";

/**
 * Account > Slimme apparaten: the customer's flexible loads (boiler,
 * vaatwasser, pomp...) and the heating nudge, all default OFF, plus the
 * evcc section that moved here from the Account tab (2026-11-02). The
 * engine turns the config into "slimme momenten" for the Prognose tab
 * and Home Assistant. docs/customer-app.md > Slimme apparaten.
 */
const KINDS: { value: SmartKind; label: string; power: number; duration: number; mode: SmartMode }[] = [
  { value: "hot_water", label: "Boiler / warm water (warmtepomp)", power: 2.5, duration: 90, mode: "deadline" },
  { value: "dishwasher", label: "Vaatwasser", power: 1.5, duration: 150, mode: "window" },
  { value: "washer", label: "Wasmachine", power: 1.5, duration: 120, mode: "window" },
  { value: "dryer", label: "Droger", power: 2.0, duration: 120, mode: "window" },
  { value: "pump", label: "Pomp (vijver, zwembad)", power: 0.3, duration: 60, mode: "daily_hours" },
  { value: "generic", label: "Ander apparaat", power: 2.0, duration: 60, mode: "window" },
];
const MODES: { value: SmartMode; label: string; help: string }[] = [
  { value: "window", label: "Binnen een dagelijks venster", help: "Het beste moment van de dag tussen begin- en eindtijd." },
  { value: "deadline", label: "Klaar vóór een tijdstip", help: "Bijvoorbeeld warm water vóór de ochtenddouche; de start ligt in de uren ervoor." },
  { value: "daily_hours", label: "Een aantal uren per dag", help: "De goedkoopste uren van de komende 24 uur, voor pompen en dergelijke." },
];

function slug(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 32) || "apparaat";
}

function newLoad(existing: SmartLoad[]): SmartLoad {
  const base = KINDS[0];
  let id = slug(base.label.split(" ")[0]);
  let n = 2;
  while (existing.some((l) => l.id === id)) id = `${slug(base.label.split(" ")[0])}-${n++}`;
  return {
    id, name: "Boiler", kind: base.value, power_kw: base.power, duration_min: base.duration, mode: base.mode,
    window_start: "10:00", window_end: "18:00", deadline: "06:30", lead_hours: 8, daily_hours: 4,
    prefer_surplus: true, enabled: true, calibrate: true,
  };
}

function fmtRun(ts: number): string {
  const d = new Date(ts * 1000);
  return d.toLocaleDateString("nl-NL", { weekday: "short", day: "numeric", month: "short" }) + " " +
    d.toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit" });
}

export function SmartDevices() {
  const [state, setState] = useState<SmartConfigResponse | null>(null);
  const [form, setForm] = useState<SmartConfigBody>({ enabled: false, heating_enabled: false, loads: [] });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const [runs, setRuns] = useState<SmartRunsResponse | null>(null);

  useEffect(() => {
    fetchSmartConfig()
      .then((s) => { setState(s); setForm({ enabled: s.enabled, heating_enabled: s.heating_enabled, loads: s.loads }); })
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));
    // Reported runs (Home Assistant): failure-soft, the section just stays empty.
    fetchSmartRuns(20).then(setRuns).catch(() => {});
  }, []);

  function patchLoad(i: number, p: Partial<SmartLoad>) {
    setForm((f) => ({ ...f, loads: f.loads.map((l, k) => (k === i ? { ...l, ...p } : l)) }));
  }
  function setKind(i: number, kind: SmartKind) {
    const k = KINDS.find((x) => x.value === kind) ?? KINDS[KINDS.length - 1];
    patchLoad(i, { kind, power_kw: k.power, duration_min: k.duration, mode: k.mode });
  }
  async function persist(fn: () => Promise<SmartConfigResponse>, msg: string) {
    setBusy(true); setError(null);
    try {
      const s = await fn();
      setState(s); setForm({ enabled: s.enabled, heating_enabled: s.heating_enabled, loads: s.loads });
      setFlash(msg); window.setTimeout(() => setFlash(null), 2000);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="tab-enter stagger px-4 pt-6 pb-28 max-w-3xl mx-auto flex flex-col gap-4 lg:px-8 lg:pt-10 lg:pb-12">
      <ScreenHeader title="Slimme apparaten" subtitle="Laat apparaten draaien op de beste momenten" />
      <Link to="/account" className="text-sm text-accent">← Account</Link>

      <section className="flex flex-col gap-2">
        <SectionTitle>Slimme momenten</SectionTitle>
        <div className="card p-4 flex flex-col gap-3">
          <label className="flex items-start justify-between gap-3 cursor-pointer">
            <span>
              <span className="block text-sm font-medium">Slimme momenten aan</span>
              <span className="block text-xs text-ink-mute leading-relaxed">
                Het systeem rekent per apparaat uit wanneer het het voordeligst draait: op zonoverschot,
                in goedkope uren, en met wat de batterij toch al doet. U ziet het op het tabblad Prognose en,
                via de Home Assistant-koppeling, als sensoren voor uw automatiseringen. Standaard uit.
              </span>
            </span>
            <input type="checkbox" className="mt-1 h-5 w-5 accent-accent shrink-0" checked={form.enabled}
              onChange={(e) => setForm((f) => ({ ...f, enabled: e.target.checked }))} />
          </label>
          {form.enabled && (
            <label className="flex items-start justify-between gap-3 cursor-pointer border-t border-card-border pt-3">
              <span>
                <span className="block text-sm font-medium">Verwarmingsadvies</span>
                <span className="block text-xs text-ink-mute leading-relaxed">
                  Advies om de verwarming een graad bij te stoken in goedkope uren en te temperen in dure uren
                  (het huis als warmtebuffer). Alleen advies: u koppelt het zelf aan uw thermostaat.
                </span>
              </span>
              <input type="checkbox" className="mt-1 h-5 w-5 accent-accent shrink-0" checked={form.heating_enabled}
                onChange={(e) => setForm((f) => ({ ...f, heating_enabled: e.target.checked }))} />
            </label>
          )}
        </div>
      </section>

      {form.enabled && (
        <section className="flex flex-col gap-2">
          <SectionTitle>Apparaten</SectionTitle>
          {form.loads.length === 0 && (
            <p className="text-xs text-ink-mute px-1">Nog geen apparaten. Voeg bijvoorbeeld uw boiler of vaatwasser toe.</p>
          )}
          {form.loads.map((l, i) => (
            <div key={l.id} className="card p-4 flex flex-col gap-3">
              <div className="flex items-center justify-between gap-2">
                <input className="flex-1 min-w-0 rounded-lg border border-line bg-transparent px-3 py-2 text-sm font-medium" value={l.name} maxLength={40}
                  onChange={(e) => patchLoad(i, { name: e.target.value })} aria-label="Naam" />
                <label className="flex items-center gap-1 text-xs text-ink-mute">
                  <input type="checkbox" className="accent-accent" checked={l.enabled}
                    onChange={(e) => patchLoad(i, { enabled: e.target.checked })} /> actief
                </label>
                <button type="button" className="text-xs text-danger" aria-label="Verwijderen"
                  onClick={() => setForm((f) => ({ ...f, loads: f.loads.filter((_, k) => k !== i) }))}>Verwijder</button>
              </div>
              {runs?.stats[l.id]?.measured ? (
                <p className="text-xs text-ink-mute">
                  Gemeten: {runs.stats[l.id].measured} keer, gemiddeld {formatEnergy(runs.stats[l.id].kwh / Math.max(1, runs.stats[l.id].runs))} per keer.
                </p>
              ) : null}
              <label className="block text-xs text-ink-mute">Soort
                <select className="mt-1 w-full rounded-lg border border-line bg-transparent px-3 py-2 text-sm" value={l.kind} onChange={(e) => setKind(i, e.target.value as SmartKind)}>
                  {KINDS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
                </select>
              </label>
              <div className="grid grid-cols-2 gap-3">
                <label className="block text-xs text-ink-mute">Vermogen (kW)
                  <input type="number" step="0.1" min="0.05" max="22" className="mt-1 w-full rounded-lg border border-line bg-transparent px-3 py-2 text-sm" value={l.power_kw}
                    onChange={(e) => patchLoad(i, { power_kw: Number(e.target.value) })} />
                </label>
                {l.mode !== "daily_hours" && (
                  <label className="block text-xs text-ink-mute">Draaitijd (minuten)
                    <input type="number" step="15" min="15" max="720" className="mt-1 w-full rounded-lg border border-line bg-transparent px-3 py-2 text-sm" value={l.duration_min}
                      onChange={(e) => patchLoad(i, { duration_min: Number(e.target.value) })} />
                  </label>
                )}
              </div>
              <label className="block text-xs text-ink-mute">Wanneer
                <select className="mt-1 w-full rounded-lg border border-line bg-transparent px-3 py-2 text-sm" value={l.mode} onChange={(e) => patchLoad(i, { mode: e.target.value as SmartMode })}>
                  {MODES.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                </select>
                <span className="block mt-1">{MODES.find((m) => m.value === l.mode)?.help}</span>
              </label>
              {l.mode === "window" && (
                <div className="grid grid-cols-2 gap-3">
                  <label className="block text-xs text-ink-mute">Van
                    <input type="time" className="mt-1 w-full rounded-lg border border-line bg-transparent px-3 py-2 text-sm" value={l.window_start} onChange={(e) => patchLoad(i, { window_start: e.target.value })} />
                  </label>
                  <label className="block text-xs text-ink-mute">Tot
                    <input type="time" className="mt-1 w-full rounded-lg border border-line bg-transparent px-3 py-2 text-sm" value={l.window_end} onChange={(e) => patchLoad(i, { window_end: e.target.value })} />
                  </label>
                </div>
              )}
              {l.mode === "deadline" && (
                <div className="grid grid-cols-2 gap-3">
                  <label className="block text-xs text-ink-mute">Klaar vóór
                    <input type="time" className="mt-1 w-full rounded-lg border border-line bg-transparent px-3 py-2 text-sm" value={l.deadline} onChange={(e) => patchLoad(i, { deadline: e.target.value })} />
                  </label>
                  <label className="block text-xs text-ink-mute">Op zijn vroegst (uren ervoor)
                    <input type="number" step="1" min="1" max="24" className="mt-1 w-full rounded-lg border border-line bg-transparent px-3 py-2 text-sm" value={l.lead_hours}
                      onChange={(e) => patchLoad(i, { lead_hours: Number(e.target.value) })} />
                  </label>
                </div>
              )}
              {l.mode === "daily_hours" && (
                <label className="block text-xs text-ink-mute">Uren per dag
                  <input type="number" step="0.5" min="0.5" max="24" className="mt-1 w-full rounded-lg border border-line bg-transparent px-3 py-2 text-sm" value={l.daily_hours}
                    onChange={(e) => patchLoad(i, { daily_hours: Number(e.target.value) })} />
                </label>
              )}
              <label className="flex items-start justify-between gap-3 cursor-pointer border-t border-card-border pt-3">
                <span>
                  <span className="block text-xs font-medium">Gemeten verbruik gebruiken</span>
                  <span className="block text-xs text-ink-mute leading-relaxed">
                    Meldt Home Assistant de draaibeurten met een energiemeting, dan vervangt het gemeten verbruik en de gemeten draaitijd na drie keer de waarden hierboven.
                  </span>
                </span>
                <input type="checkbox" className="mt-0.5 h-4 w-4 accent-accent shrink-0" checked={l.calibrate ?? true}
                  onChange={(e) => patchLoad(i, { calibrate: e.target.checked })} />
              </label>
            </div>
          ))}
          {form.loads.length < 12 && (
            <button type="button" className="self-start text-sm text-accent border border-accent rounded-full px-4 py-2 font-medium"
              onClick={() => setForm((f) => ({ ...f, loads: [...f.loads, newLoad(f.loads)] }))}>+ Apparaat toevoegen</button>
          )}
        </section>
      )}

      {form.enabled && runs && runs.runs.length > 0 && (
        <section className="flex flex-col gap-2">
          <SectionTitle>Gedraaid</SectionTitle>
          <div className="card p-4 flex flex-col gap-3">
            <p className="text-xs text-ink-mute leading-relaxed">
              Wat Home Assistant meldde over de afgelopen {Object.values(runs.stats)[0]?.days ?? 30} dagen, en of het in het aanbevolen venster viel.
            </p>
            {Object.entries(runs.stats).map(([lid, st]) => {
              const name = form.loads.find((l) => l.id === lid)?.name ?? lid;
              return (
                <div key={lid} className="text-sm border-t border-card-border pt-2">
                  <div className="font-medium">{name}</div>
                  <div className="text-xs text-ink-mute">
                    {st.runs} keer gedraaid
                    {st.scored > 0 && <>, {st.followed} van {st.scored} op het aanbevolen moment</>}
                    {st.kwh > 0 && <>, {formatEnergy(st.kwh)}</>}
                    {st.saving_eur > 0.005 && <>, {formatEur(st.saving_eur)} bespaard</>}
                    {st.running_since != null && <>, loopt nu sinds {fmtRun(st.running_since)}</>}
                  </div>
                </div>
              );
            })}
            <ul className="text-xs text-ink-mute flex flex-col gap-1 border-t border-card-border pt-2">
              {runs.runs.slice(0, 8).map((r) => (
                <li key={`${r.load_id}-${r.started_at}`} className="flex justify-between gap-2">
                  <span>
                    {form.loads.find((l) => l.id === r.load_id)?.name ?? r.load_id} {fmtRun(r.started_at)}
                    {r.ended_at == null ? " (loopt)" : ""}
                    {r.kwh != null ? `, ${formatEnergy(r.kwh)}` : r.kwh_used != null ? `, ca. ${formatEnergy(r.kwh_used)}` : ""}
                  </span>
                  <span className={r.in_window ? "text-accent" : ""}>
                    {r.in_window == null ? "" : r.in_window ? "aanbevolen moment" : "ander moment"}
                    {r.saving_eur != null && r.saving_eur > 0.005 ? ` +${formatEur(r.saving_eur)}` : ""}
                  </span>
                </li>
              ))}
            </ul>
            <button type="button" className="self-start text-xs text-ink-mute" disabled={busy}
              onClick={() => clearSmartRuns().then(setRuns).catch((e) => setError(e instanceof Error ? e.message : String(e)))}>
              Historie wissen
            </button>
          </div>
        </section>
      )}

      <div className="flex items-center gap-3 px-1">
        <button type="button" className="bg-accent text-white text-sm rounded-full px-4 py-2 font-medium disabled:opacity-60" disabled={busy}
          onClick={() => persist(() => saveSmartConfig(form), "Opgeslagen")}>Opslaan</button>
        {state?.source === "override" && (
          <button type="button" className="text-sm text-ink-mute" disabled={busy}
            onClick={() => persist(resetSmartConfig, "Alles uitgezet")}>Alles uitzetten</button>
        )}
        {flash && <span className="text-xs text-accent">{flash}</span>}
      </div>
      {error && <p className="text-sm text-danger bg-danger-soft rounded-xl p-3 border border-card-border">{error}</p>}

      <EvSettings />
    </div>
  );
}
